import { useState, useEffect } from 'react'
import { type PromoBadge } from '@repo/supabase'
import { supabase } from '../supabaseClient'
import { useInvestmentContext } from '../contexts/InvestmentContext'

interface UsePromoBadgesResult {
  badges: PromoBadge[]
  moduleEnabled: boolean
  tenantId: string | null
  isLoaded: boolean
  reload: () => void
}

/**
 * Loads the current tenant's custom promo badges (module: has_module_price_badge).
 * One query for the whole property list — passed down to each card.
 */
export const usePromoBadges = (): UsePromoBadgesResult => {
  const { currentInvestment } = useInvestmentContext()
  const tenantId = currentInvestment?.tenant?.id ?? null
  const moduleEnabled = currentInvestment?.tenant?.has_module_price_badge ?? false

  const [badges, setBadges] = useState<PromoBadge[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!tenantId || !moduleEnabled) {
      setBadges([])
      setIsLoaded(true)
      return
    }

    let cancelled = false
    setIsLoaded(false)

    supabase
      .from('tenant_promo_badges')
      .select('*')
      .eq('tenant_id', tenantId)
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          console.error('[usePromoBadges] load failed:', error.message)
          setBadges([])
        } else {
          setBadges(data ?? [])
        }
        setIsLoaded(true)
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, moduleEnabled, reloadKey])

  return {
    badges,
    moduleEnabled,
    tenantId,
    isLoaded,
    reload: () => setReloadKey(k => k + 1)
  }
}
